import { useCallback, useEffect, useRef, useState } from 'react'
import { log } from '#utils/logger'

type HoverEventHandler<TPath extends string> = (newPath: TPath) => unknown

export type HoveredEntryResult<TPath extends string> = ReturnType<typeof useHoveredEntry<TPath>>
export type HoverEventBus<TPath extends string> = HoveredEntryResult<TPath>['eventBus']['current']

export function useHoveredEntry<TPath extends string>({ delay = 500, onHoveredEntry }: { delay?: number; onHoveredEntry?: (path: TPath) => void }) {
  const onHoveredEntryRef = useRef(onHoveredEntry)

  const eventBus = useRef({
    path: '' as TPath,
    lastTime: undefined as ReturnType<typeof setTimeout> | undefined,
    handlers: [] as HoverEventHandler<TPath>[],
    addEventListener: (handler: HoverEventHandler<TPath>) => eventBus.current.handlers.push(handler),
    removeEventListener: (handler: HoverEventHandler<TPath>) => {
      eventBus.current.handlers = eventBus.current.handlers.filter((x) => x !== handler)
    },
  })

  const updateHoveredEntry = useCallback(
    (path: TPath) => {
      for (const handler of eventBus.current.handlers) {
        try {
          handler(path)
        } catch (e) {
          log.warn('Exception caught in Hash Updater with: ', e)
        }
      }
      eventBus.current.path = path

      // this is the seemingly only way to change the hash location without React Router re-rendering the page
      window.history.replaceState(window.history.state, '', `#${path}`)

      if (eventBus.current.lastTime) {
        clearTimeout(eventBus.current.lastTime)
        eventBus.current.lastTime = undefined
      }
      eventBus.current.lastTime = setTimeout(() => {
        eventBus.current.lastTime = undefined
        onHoveredEntryRef.current?.(path)
      }, delay)
    },
    [delay],
  )

  return {
    eventBus,
    updateHoveredEntry,
  }
}

export function useCurrentHoverPath<TPath extends string>(hoverEventBus: HoverEventBus<TPath> | undefined, fallbackPath: TPath = '' as TPath): TPath {
  const [path, setPath] = useState(hoverEventBus?.path ?? (fallbackPath as TPath))
  useEffect(() => {
    if (!hoverEventBus) {
      return
    }
    setPath(hoverEventBus.path)
    hoverEventBus.addEventListener(setPath)

    return () => hoverEventBus.removeEventListener(setPath)
  }, [hoverEventBus])

  return path ?? fallbackPath
}
